import {t, tpl} from '../../i18n/i18n.js';
import {syncFieldGroupVisibility} from './field-references.js';
import {syncRequiredIndicator} from './field-renderers.js';
import {createBandGroup, createCardFrame, createCardMenu, createChoiceGroups, createDeleteConfirm, createItemAdder, createItemMenu} from './section-cards.js';
import {createPageOutline} from './page-outline.js';
import {createBlockPicker, createRowFrame, setRowTone} from './section-rows.js';
import '../section-item-editor/section-item-editor.component.js';
import {
    addItem,
    appearanceApplies,
    blankItem,
    compositionRows,
    defaultCompositionState,
    dissolveRow,
    duplicateItem,
    entrySpan,
    fieldOwnership,
    hasBands,
    hasRowPlacement,
    insertItemAt,
    isRepeatable,
    isSectionEnabled,
    itemCount,
    itemRef,
    layoutFor,
    moveSection,
    moveSectionTo,
    pairablePrevious,
    pairWithPrevious,
    parseEntry,
    removeItem,
    repairRows,
    requiredFieldKeys,
    restoreCompositionState,
    rowBand,
    rowTones,
    sectionContentProblems,
    selectAppearance,
    selectBand,
    selectLayout,
    serializeCompositionState,
    setSplit,
    splitBounds,
    swapRow,
    validateComposition,
    visibleFieldKeys,
} from '../../core/page/composition.core.js';
import {newRowId} from '../../core/page/page.core.js';
import {
    COMPOSITION_DATA_KEY,
    FieldType,
    TABLE_ROW_ID_KEY,
    imageCompanionKeys,
    isTextFieldType,
} from '../../core/vocabulary.js';

const EXCERPT_LENGTH = 70;

/**
 * @typedef {import('../../shell/types/user.types.js').CompositionSection} CompositionSection
 * @typedef {import('../section-item-editor/section-item-editor.component.js').PendingItemFile} PendingItemFile
 * @typedef {{item: Object<string, string>, files: PendingItemFile[]}} ItemValues Values of one item
 *   and its pending uploads, keyed by item reference where a map of them is passed around
 */

/**
 * Renders the "page content" section list for templates with a composition. Field groups
 * already rendered by the page form are moved into their section's card so they stay inside
 * the same <form> and keep their values, uploads and focal points while cards open, close or
 * move. Emits no network calls; the page form serializes the state via a hidden input.
 *
 * Cards are keyed by entry: a plain section id, or an item reference for each item of a
 * repeatable section. An item card holds a `<section-item-editor>` with the item's fields; the
 * page form reads the items from those editors (`data-list-key`, `data-item-id`) in DOM order.
 * Each repeatable section gets an add control below the list.
 */
export class SectionEditor {
    #root;
    #container;
    #composition;
    #fields;
    #fieldsByKey;
    #ownership;
    #state;
    #compatible = true;
    #showErrors = false;
    #onChange;
    #onAssetRequest;
    /** @type {Map<string, ItemValues>} Values the item cards are first rendered with */
    #initialItems;
    #panel = null;
    #list = null;
    #hiddenInput = null;
    #liveRegion = null;
    #incompatibleNotice = null;
    #rowNotice = null;
    #outline = null;
    #activeEntry = null;
    /** @type {import('../../core/page/composition.core.js').CompositionRow[] | undefined} rows as of the last sync, before any later change */
    #syncedRows;
    /** @type {Map<string, HTMLElement>} keyed by entry */
    #cards = new Map();
    /** @type {Map<string, ReturnType<typeof createRowFrame>>} row frames keyed by the row's first entry */
    #rowFrames = new Map();
    /** @type {Map<string, import('../section-item-editor/section-item-editor.component.js').SectionItemEditor>} keyed by item reference */
    #itemEditors = new Map();
    /** @type {Map<string, HTMLElement>} add controls keyed by section id */
    #adders = new Map();
    #draggedId = null;
    #thumbnailUrls = new Map();

    /**
     * A given `state` (the previous editor's, when the form is rebuilt) is kept when it still
     * fits the composition, together with `compatible` and the item values in `items`;
     * otherwise the editor starts from the template defaults with fresh blank items.
     *
     * @param {{
     *   root: ShadowRoot,
     *   container: HTMLElement,
     *   composition: import('../../shell/types/user.types.js').TemplateComposition,
     *   fields: import('../../shell/types/user.types.js').TemplateField[],
     *   state?: import('../../shell/types/user.types.js').CompositionState | null,
     *   compatible?: boolean,
     *   items?: Map<string, ItemValues> | null,
     *   onChange?: () => void,
     *   onAssetRequest?: (path: string, img: HTMLImageElement) => void,
     * }} options
     */
    constructor({root, container, composition, fields, state = null, compatible = true, items = null, onChange = () => {}, onAssetRequest = () => {}}) {
        this.#root = root;
        this.#container = container;
        this.#composition = composition;
        this.#fields = fields || [];
        this.#fieldsByKey = new Map(this.#fields.map((field) => [field.key, field]));
        this.#ownership = fieldOwnership(composition, this.#fields);
        this.#onChange = onChange;
        this.#onAssetRequest = onAssetRequest;
        const values = items ?? new Map();
        const carried = state ? this.#carriedState(state, values) : null;
        if (carried) {
            this.#state = carried;
            this.#compatible = compatible;
            this.#initialItems = values;
        } else {
            const defaults = defaultCompositionState(composition, {}, newRowId);
            this.#state = defaults.state;
            this.#initialItems = this.#itemValuesFromLists(defaults.lists);
        }
    }

    /** @returns {import('../../shell/types/user.types.js').CompositionState} */
    get state() {
        return this.#state;
    }

    get compatible() {
        return this.#compatible;
    }

    /** Field keys whose required-ness is governed by the composition. */
    get ownedKeys() {
        return new Set(this.#ownership.keys());
    }

    /**
     * Current values and pending uploads of every item, keyed by item reference, for carrying
     * them into the editor of a rebuilt form.
     * @returns {Map<string, ItemValues>}
     */
    get itemValues() {
        const values = new Map();
        for (const [ref, editor] of this.#itemEditors) {
            values.set(ref, {item: editor.item, files: editor.files});
        }
        return values;
    }

    render() {
        this.#panel = document.createElement('section');
        this.#panel.className = 'composition-panel';
        this.#panel.setAttribute('aria-labelledby', 'composition-title');

        const title = document.createElement('h3');
        title.id = 'composition-title';
        title.className = 'composition-title';
        title.dataset.i18n = 'pageForm.compositionTitle';
        title.textContent = t('pageForm.compositionTitle');

        const hint = document.createElement('p');
        hint.className = 'composition-hint';
        const hintKey = this.#composition.fixedGroups?.length
            ? 'pageForm.compositionHintFixed'
            : 'pageForm.compositionHint';
        hint.dataset.i18n = hintKey;
        hint.textContent = t(hintKey);

        this.#incompatibleNotice = document.createElement('p');
        this.#incompatibleNotice.className = 'composition-incompatible';
        this.#incompatibleNotice.setAttribute('role', 'alert');
        this.#incompatibleNotice.dataset.i18n = 'pageForm.compositionIncompatible';
        this.#incompatibleNotice.textContent = t('pageForm.compositionIncompatible');
        this.#incompatibleNotice.hidden = this.#compatible;

        this.#rowNotice = document.createElement('li');
        this.#rowNotice.className = 'composition-row-notice';
        this.#rowNotice.setAttribute('aria-hidden', 'true');
        this.#rowNotice.dataset.i18n = 'pageForm.rowDissolved';
        this.#rowNotice.textContent = t('pageForm.rowDissolved');
        this.#rowNotice.hidden = true;

        this.#list = document.createElement('ol');
        this.#list.className = 'section-list';
        this.#list.addEventListener('dragover', (event) => this.#onDragOver(event));
        this.#list.addEventListener('drop', (event) => this.#onDrop(event));

        this.#liveRegion = document.createElement('p');
        this.#liveRegion.className = 'sr-only';
        this.#liveRegion.setAttribute('aria-live', 'polite');

        this.#hiddenInput = document.createElement('input');
        this.#hiddenInput.type = 'hidden';
        this.#hiddenInput.name = COMPOSITION_DATA_KEY;

        this.#panel.append(title, hint, this.#incompatibleNotice);
        const main = document.createElement('div');
        main.className = 'composition-main';
        main.appendChild(this.#list);
        const repeatables = this.#repeatableSections();
        if (repeatables.length > 0) {
            const adders = document.createElement('div');
            adders.className = 'section-item-adders';
            adders.setAttribute('role', 'group');
            adders.setAttribute('aria-labelledby', 'section-item-adders-title');
            const addersTitle = document.createElement('p');
            addersTitle.id = 'section-item-adders-title';
            addersTitle.className = 'section-item-adders-title';
            addersTitle.dataset.i18n = 'pageForm.addBlockTitle';
            addersTitle.textContent = t('pageForm.addBlockTitle');
            const chips = document.createElement('div');
            chips.className = 'section-item-adders-list';
            adders.append(addersTitle, chips);
            for (const section of repeatables) {
                const adder = createItemAdder(section, () => this.#add(section.id));
                this.#adders.set(section.id, adder);
                chips.appendChild(adder);
            }
            main.appendChild(adders);
        }
        if (hasRowPlacement(this.#composition)) {
            const layout = document.createElement('div');
            layout.className = 'composition-layout';
            this.#outline = createPageOutline({onSelect: (entry) => this.#jumpTo(entry)});
            this.#outline.element.open = !window.matchMedia?.('(max-width: 1280px)').matches;
            layout.append(main, this.#outline.element);
            this.#panel.appendChild(layout);
        } else {
            this.#panel.appendChild(main);
        }
        this.#panel.append(this.#liveRegion, this.#hiddenInput);

        for (const section of this.#plainSections()) {
            this.#cards.set(section.id, this.#createCard(section));
        }
        this.#createItemCards(this.#initialItems);
        const placementAnchor = this.#container.querySelector(':scope > .layout-context-card');
        if (placementAnchor) {
            placementAnchor.insertAdjacentElement('beforebegin', this.#panel);
        } else {
            this.#container.appendChild(this.#panel);
        }
        for (const section of this.#plainSections()) {
            this.#adoptFieldGroups(section, this.#cards.get(section.id));
        }
        this.#reorderCards();
        this.#syncAll();
    }

    /** Move existing editor groups into the page-content list without changing saved section order. */
    placeFixedGroups() {
        if (!this.#list) return;
        const movableAnchor = this.#list.querySelector(':scope > .section-card');
        for (const label of this.#composition.fixedGroups || []) {
            const group = [...this.#container.querySelectorAll(':scope > .editor-group')]
                .find((item) => item.getAttribute('aria-label') === label);
            if (!group) continue;
            const item = document.createElement('li');
            item.className = 'fixed-section-card';
            item.dataset.fixedGroup = label;
            item.appendChild(group);
            this.#list.insertBefore(item, movableAnchor);
        }
    }

    /**
     * Restores saved page state (edit mode) and the items of the saved lists. Incompatible
     * saved state keeps the template defaults on screen (with the items the lists hold), leaves
     * the hidden input empty so nothing is overwritten, and blocks submission until an
     * administrator migrates the page.
     * @param {unknown} saved
     * @param {Object<string, unknown>} [data] Page data holding the item lists under their data keys
     */
    restore(saved, data = {}) {
        const lists = {};
        for (const section of this.#repeatableSections()) {
            lists[section.repeatable.dataKey] = data[section.repeatable.dataKey];
        }
        const {state, compatible} = restoreCompositionState(this.#composition, saved, lists,
            (key) => this.#isChecked(key));
        this.#hideRowNotice();
        this.#state = state;
        this.#compatible = compatible;
        this.#initialItems = this.#itemValuesFromLists(lists);
        if (this.#incompatibleNotice) this.#incompatibleNotice.hidden = compatible;
        if (this.#list) {
            for (const ref of [...this.#itemEditors.keys()]) this.#dropItemCard(ref);
            this.#createItemCards(this.#initialItems);
        }
        this.#reorderCards();
        this.#syncAll();
    }

    /** Re-evaluates statuses, excerpts, visibility and errors after field edits. */
    refresh() {
        const dissolved = this.#repairRows();
        this.#syncAll();
        if (dissolved) this.#announceRowDissolved(dissolved);
    }

    /** Whether the current state can be submitted without validation errors. */
    isReady() {
        return this.#compatible && this.#problems().length === 0 && this.#countProblems().length === 0;
    }

    /**
     * Shows content problems on the affected cards. With `focus`, opens the first failing card
     * and focuses its first missing field, or, for an item count outside the section's limits,
     * focuses the add button (too few) or the section's first item (too many).
     * @param {{focus?: boolean}} [options]
     * @returns {boolean} true when the composition is valid
     */
    validate({focus = false} = {}) {
        this.#showErrors = true;
        this.#syncAll();
        if (!this.#compatible) {
            if (focus) this.#incompatibleNotice?.scrollIntoView?.({block: 'nearest'});
            return false;
        }
        const problems = this.#problems();
        const counts = this.#countProblems();
        if (problems.length === 0 && counts.length === 0) return true;
        if (focus) {
            if (problems.length > 0) {
                this.#focusProblem(problems[0]);
            } else {
                this.#focusCountProblem(counts[0]);
            }
        }
        return false;
    }

    #focusProblem(problem) {
        if (problem.ref) {
            this.#expand(problem.ref);
            this.#itemEditors.get(problem.ref).focusField(problem.keys[0]);
            return;
        }
        this.#expand(problem.sectionId);
        const group = this.#fieldGroup(problem.keys[0]);
        const target = group?.querySelector('input:not([type="hidden"]):not(.file-input-hidden), textarea, select, button, table-field');
        target?.focus();
    }

    #focusCountProblem({section, tooFew}) {
        if (tooFew) {
            this.#addButton(section.id).focus();
            return;
        }
        const first = this.#state.order.find((entry) => parseEntry(entry).sectionId === section.id);
        this.#cards.get(first).querySelector('.section-edit').focus();
    }

    // ---- card construction -------------------------------------------------------------

    #createCard(section) {
        const {card, header, actions, body} = this.#createFrame(section.id, section.id, section.label, true);
        const rowActions = this.#rowActions(section.id, header);
        if (rowActions.length > 0) {
            actions.appendChild(createCardMenu({menuId: `section-menu-${section.id}`, actions: rowActions}).wrap);
        }
        body.append(...this.#choiceGroups(section, section.id));
        if (hasBands(this.#composition)) {
            body.appendChild(createBandGroup(this.#composition, `__wb_band_${section.id}`,
                (bandId) => this.#updateChoice(selectBand(this.#state, section.id, bandId))));
        }

        const instruction = document.createElement('div');
        instruction.className = 'section-card-instruction';
        instruction.hidden = true;

        const fieldsBox = document.createElement('div');
        fieldsBox.className = 'section-card-fields';

        body.append(instruction, fieldsBox);
        return card;
    }

    /**
     * The row actions of a card's menu in a template that places rows: "next to the block above"
     * on every card (disabled by the row sync when either section has no span, among other
     * reasons) and, when the template has repeatable sections, "add block below" with its block
     * picker placed after the card header. Templates without spans get neither, so their editor
     * is unchanged.
     */
    #rowActions(entry, header) {
        if (!hasRowPlacement(this.#composition)) return [];
        const actions = [{className: 'section-pair-previous', i18nKey: 'pageForm.pairWithPrevious', onSelect: () => this.#pairWithPrevious(entry)}];
        const blocks = this.#repeatableSections();
        if (blocks.length > 0) {
            const picker = createBlockPicker({
                id: `section-picker-${entry}`,
                sections: blocks,
                onPick: (sectionId) => this.#addBelow(entry, sectionId),
                onCancel: () => this.#closePicker(entry, true),
            });
            header.insertAdjacentElement('afterend', picker);
            actions.push({className: 'section-add-below', i18nKey: 'pageForm.addBelow', onSelect: () => this.#openPicker(entry)});
        }
        return actions;
    }

    #createFrame(entry, sectionId, labelText, withToggle) {
        const frame = createCardFrame({
            entry,
            sectionId,
            labelText,
            withToggle,
            isDragging: () => Boolean(this.#draggedId),
            onDragStart: (event) => this.#onDragStart(event, entry),
            onDragEnd: () => this.#onDragEnd(frame.card),
            onMove: (delta, focusSelector) => this.#move(entry, delta, focusSelector),
            onToggle: (expanded) => (expanded ? this.#collapse(entry) : this.#expand(entry)),
        });
        return frame;
    }

    #choiceGroups(section, entry) {
        return createChoiceGroups(section, entry, {
            layout: (layoutId) => this.#updateChoice(selectLayout(this.#state, entry, layoutId)),
            appearance: (appearanceId) => this.#updateChoice(selectAppearance(this.#state, entry, appearanceId)),
        });
    }

    /** Creates the cards of the items in the state, filled from `values`. */
    #createItemCards(values) {
        for (const entry of this.#state.order) {
            if (parseEntry(entry).itemId !== null) this.#createItemCard(entry, values.get(entry));
        }
    }

    /**
     * An item card: the card frame with a menu (duplicate, delete) and an inline delete
     * confirmation, and a body with the item's choices and its item editor.
     * @param {string} ref
     * @param {ItemValues} [values]
     */
    #createItemCard(ref, values) {
        const {sectionId, itemId} = parseEntry(ref);
        const section = this.#section(sectionId);
        const {card, header, actions, body} = this.#createFrame(ref, sectionId, '', false);
        card.classList.add('section-card--item');
        card.dataset.itemRef = ref;

        const menu = createItemMenu({
            menuId: `section-menu-${ref}`,
            hintId: `section-menu-hint-${ref}`,
            onDuplicate: () => this.#duplicate(ref),
            onDelete: () => this.#askDelete(ref),
            extraActions: this.#rowActions(ref, header),
        });
        actions.appendChild(menu.wrap);
        const confirm = createDeleteConfirm({
            questionId: `section-confirm-${ref}`,
            onConfirm: () => this.#remove(ref),
            onCancel: () => this.#cancelDelete(ref),
        });
        header.insertAdjacentElement('afterend', confirm);

        body.append(...this.#choiceGroups(section, ref));
        if (hasBands(this.#composition)) {
            body.appendChild(createBandGroup(this.#composition, `__wb_band_${ref}`,
                (bandId) => this.#updateChoice(selectBand(this.#state, ref, bandId))));
        }
        const instruction = document.createElement('div');
        instruction.className = 'section-card-instruction';
        instruction.hidden = true;

        const editor = document.createElement('section-item-editor');
        editor.dataset.listKey = section.repeatable.dataKey;
        editor.dataset.itemId = itemId;
        editor.fields = section.repeatable.fields;
        editor.item = values?.item ?? {[TABLE_ROW_ID_KEY]: itemId};
        editor.files = values?.files ?? [];
        body.append(instruction, editor);

        this.#cards.set(ref, card);
        this.#itemEditors.set(ref, editor);
        return card;
    }

    #dropItemCard(ref) {
        const card = this.#cards.get(ref);
        card.remove();
        const url = this.#thumbnailUrls.get(card);
        if (url) URL.revokeObjectURL(url);
        this.#thumbnailUrls.delete(card);
        this.#cards.delete(ref);
        this.#itemEditors.delete(ref);
    }

    /** Moves the page form's field groups for this section into its card. */
    #adoptFieldGroups(section, card) {
        const fieldsBox = card.querySelector('.section-card-fields');
        for (const key of section.fields || []) {
            for (const groupKey of [key, ...imageCompanionKeys(key)]) {
                if (groupKey !== key && this.#ownership.get(groupKey) !== section.id) continue;
                const group = this.#fieldGroup(groupKey);
                if (group) {
                    fieldsBox.appendChild(group);
                    this.#releaseNativeRequired(group);
                }
            }
        }
        if (section.enabledByField) {
            const toggleGroup = this.#fieldGroup(section.enabledByField);
            if (toggleGroup) {
                card.querySelector('.section-card-toggle').appendChild(toggleGroup);
                this.#releaseNativeRequired(toggleGroup);
            }
        }
    }

    /**
     * Section fields follow layout-specific rules, and hidden required controls would block
     * native form submission, so their native `required` flags are removed.
     */
    #releaseNativeRequired(group) {
        group.querySelectorAll('[required]').forEach((element) => element.removeAttribute('required'));
        group.querySelectorAll('.required-indicator').forEach((element) => element.remove());
    }

    #fieldGroup(key) {
        return this.#root.querySelector(`.field-group[data-field-key="${key}"]`);
    }

    // ---- state changes -----------------------------------------------------------------

    #updateChoice(state) {
        this.#state = state;
        this.#syncAll();
        this.#onChange();
    }

    #move(entry, delta, focusSelector) {
        const next = moveSection(this.#state, entry, delta);
        if (next === this.#state) return;
        this.#hideRowNotice();
        const label = this.#entryLabel(entry);
        this.#state = next;
        this.#reorderCards();
        this.#announceMove(entry, label);
        const dissolved = this.#repairRows();
        this.#syncAll();
        if (dissolved) this.#announceRowDissolved(dissolved);
        const card = this.#cards.get(entry);
        let target = card.querySelector(focusSelector);
        if (target.disabled) {
            target = card.querySelector(focusSelector === '.section-move-up' ? '.section-move-down' : '.section-move-up');
        }
        target.focus();
        this.#onChange();
    }

    /**
     * Announces the new position of a moved entry under the name it had before the move: an
     * item titled by its number is renumbered by the move itself.
     */
    #announceMove(entry, label) {
        this.#announce(tpl('pageForm.sectionMoved', {
            label,
            position: this.#state.order.indexOf(entry) + 1,
            total: this.#state.order.length,
        }));
    }

    #announce(text) {
        if (!this.#liveRegion) return;
        this.#liveRegion.textContent = text;
    }

    /**
     * Announces a dissolved row and shows the visible notice above `entry`, the first card of the
     * row; used as the last message of a move, drop, unpair or refresh.
     */
    #announceRowDissolved(entry) {
        this.#announce(t('pageForm.rowDissolved'));
        this.#showRowNotice(entry);
    }

    /**
     * Announces `key`/`params`, with the row-unpaired text appended when the same action also
     * dissolved a row (an add or a delete has its own subject, so the two messages combine rather
     * than one overwriting the other); shows the visible notice above `dissolvedEntry` in that case.
     */
    #announceChange(key, params, dissolvedEntry) {
        const message = tpl(key, params);
        if (!dissolvedEntry) {
            this.#announce(message);
            return;
        }
        this.#announce(`${message}. ${t('pageForm.rowDissolved')}`);
        this.#showRowNotice(dissolvedEntry);
    }

    /**
     * Shows the notice directly above the card of `entry`, where the row was dissolved, so it is
     * seen even when that row is far down the list. It stays until the next structural change.
     */
    #showRowNotice(entry) {
        if (!this.#rowNotice) return;
        this.#rowNotice.textContent = t('pageForm.rowDissolved');
        this.#rowNotice.hidden = false;
        this.#cards.get(entry).insertAdjacentElement('beforebegin', this.#rowNotice);
    }

    /**
     * Hides the notice at the start of a structural change (move, add, delete, duplicate, pair,
     * split, swap, unpair or restore) and takes it out of the list, so it never sits between a
     * row frame and the row's first card.
     */
    #hideRowNotice() {
        if (!this.#rowNotice) return;
        this.#rowNotice.hidden = true;
        this.#rowNotice.remove();
    }

    /**
     * Adds a blank item to a repeatable section, opens its card and focuses its first field. With
     * no `index`, its position is pushed past a shared row when it would otherwise land inside one
     * (see {@link addItem}), so adding at the bottom of the list never breaks a row; with an
     * `index` (from "add block below", already placed after the row it follows), it is inserted
     * there directly.
     */
    #add(sectionId, index) {
        const section = this.#section(sectionId);
        if (this.#atMaximum(section)) return;
        const itemId = newRowId(this.#itemIds(sectionId));
        const ref = itemRef(sectionId, itemId);
        this.#hideRowNotice();
        this.#state = index === undefined
            ? addItem(this.#state, this.#composition, sectionId, itemId, (key) => this.#isChecked(key))
            : insertItemAt(this.#state, this.#composition, sectionId, itemId, index);
        const dissolved = this.#repairRows();
        this.#createItemCard(ref, {item: blankItem(section.repeatable, itemId), files: []});
        this.#reorderCards();
        this.#syncAll();
        this.#expand(ref);
        const firstKey = [...visibleFieldKeys(section, this.#state.items[ref].layout, section.repeatable.fields)][0];
        this.#itemEditors.get(ref).focusField(firstKey);
        this.#announceChange('pageForm.itemAdded', {label: this.#entryLabel(ref)}, dissolved);
        this.#onChange();
    }

    #pairWithPrevious(entry) {
        const isChecked = (key) => this.#isChecked(key);
        const previous = pairablePrevious(this.#composition, this.#state, entry, isChecked);
        if (previous === null) return;
        this.#hideRowNotice();
        this.#state = pairWithPrevious(this.#composition, this.#state, entry, isChecked);
        this.#syncAll();
        this.#rowFrames.get(previous).element.querySelector('.split-bar-handle').focus();
        this.#announce(tpl('pageForm.rowPaired', {first: this.#entryLabel(previous), second: this.#entryLabel(entry)}));
        this.#onChange();
    }

    #openPicker(entry) {
        const picker = this.#cards.get(entry).querySelector('.section-block-picker');
        picker.hidden = false;
        (picker.querySelector('.section-block-picker-option:not(:disabled)')
            ?? picker.querySelector('.section-block-picker-cancel')).focus();
    }

    #closePicker(entry, focusMenu) {
        const card = this.#cards.get(entry);
        card.querySelector('.section-block-picker').hidden = true;
        if (focusMenu) card.querySelector('.section-item-more').focus();
    }

    /** Adds a blank item of `sectionId` after the row of `entry`. */
    #addBelow(entry, sectionId) {
        this.#closePicker(entry, false);
        const index = this.#state.order.indexOf(this.#rowEnd(entry)) + 1;
        this.#add(sectionId, index);
    }

    /**
     * Inserts a copy of an item (values, stored assets, pending files, choices) after the last
     * entry of its row, so the copy never joins a shared row.
     */
    #duplicate(ref) {
        const {sectionId} = parseEntry(ref);
        if (this.#atMaximum(this.#section(sectionId))) return;
        const itemId = newRowId(this.#itemIds(sectionId));
        const copyRef = itemRef(sectionId, itemId);
        const source = this.#itemEditors.get(ref);
        this.#hideRowNotice();
        this.#state = duplicateItem(this.#state, ref, itemId, this.#rowEnd(ref));
        this.#createItemCard(copyRef, {item: {...source.item, [TABLE_ROW_ID_KEY]: itemId}, files: source.files});
        this.#reorderCards();
        this.#syncAll();
        this.#cards.get(copyRef).querySelector('.section-edit').focus();
        this.#announce(tpl('pageForm.itemDuplicated', {label: this.#entryLabel(ref)}));
        this.#onChange();
    }

    /**
     * The last entry of the row `entry` is in (itself when it is alone in its row, or when it is a
     * hidden plain section that no row includes: a repeatable section can never declare
     * `enabledByField`, so an item's own row is always found, but a plain section's "add below" can
     * still be reached while it is hidden).
     */
    #rowEnd(entry) {
        const row = compositionRows(this.#composition, this.#state, (key) => this.#isChecked(key))
            .find((candidate) => candidate.entries.includes(entry));
        return row ? row.entries.at(-1) : entry;
    }

    /** Opens the delete confirmation of one item and closes any other that is open. */
    #askDelete(ref) {
        this.#list.querySelectorAll('.section-item-confirm').forEach((other) => {
            other.hidden = true;
        });
        const confirm = this.#cards.get(ref).querySelector('.section-item-confirm');
        confirm.hidden = false;
        confirm.querySelector('.section-item-confirm-cancel').focus();
    }

    #cancelDelete(ref) {
        const card = this.#cards.get(ref);
        card.querySelector('.section-item-confirm').hidden = true;
        card.querySelector('.section-item-more').focus();
    }

    /**
     * Removes an item; focus moves to the card now in its place, the one before, or the add
     * button. Nothing happens at the section's minimum item count.
     */
    #remove(ref) {
        const {sectionId} = parseEntry(ref);
        if (itemCount(this.#state, sectionId) <= this.#minItems(this.#section(sectionId))) return;
        const index = this.#state.order.indexOf(ref);
        const label = this.#entryLabel(ref);
        this.#hideRowNotice();
        this.#state = removeItem(this.#state, ref);
        const dissolved = this.#repairRows();
        this.#dropItemCard(ref);
        this.#syncAll();
        const neighbour = this.#state.order[index] ?? this.#state.order[index - 1];
        const target = neighbour
            ? this.#cards.get(neighbour).querySelector('.section-edit')
            : this.#addButton(sectionId);
        target.focus();
        this.#announceChange('pageForm.itemDeleted', {label}, dissolved);
        this.#onChange();
    }

    #expand(entry) {
        for (const id of this.#cards.keys()) {
            if (id !== entry) this.#collapse(id);
        }
        const card = this.#cards.get(entry);
        card.querySelector('.section-card-body').hidden = false;
        card.classList.add('is-expanded');
        const edit = card.querySelector('.section-edit');
        edit.setAttribute('aria-expanded', 'true');
        edit.textContent = t('pageForm.sectionClose');
        this.#activeEntry = entry;
        this.#syncOutline();
    }

    #collapse(entry) {
        const card = this.#cards.get(entry);
        card.querySelector('.section-card-body').hidden = true;
        card.classList.remove('is-expanded');
        const edit = card.querySelector('.section-edit');
        edit.setAttribute('aria-expanded', 'false');
        edit.textContent = t('pageForm.sectionEdit');
        if (entry === this.#activeEntry) this.#activeEntry = null;
        this.#syncOutline();
    }

    #reorderCards() {
        if (!this.#list) return;
        for (const entry of this.#state.order) {
            const frame = this.#rowFrames.get(entry);
            if (frame) this.#list.appendChild(frame.element);
            this.#list.appendChild(this.#cards.get(entry));
        }
    }

    // ---- drag and drop (pointer enhancement; buttons remain the primary control) -------

    #onDragStart(event, entry) {
        if (!event.currentTarget.draggable) {
            event.preventDefault();
            return;
        }
        this.#draggedId = entry;
        event.currentTarget.classList.add('is-dragging');
        event.dataTransfer?.setData('text/plain', entry);
        if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
    }

    #onDragOver(event) {
        if (!this.#draggedId) return;
        event.preventDefault();
        const index = this.#dropIndex(event.clientY);
        this.#cards.forEach((card) => card.classList.remove('is-drop-before', 'is-drop-after'));
        const targetId = this.#state.order[Math.min(index, this.#state.order.length - 1)];
        const targetCard = this.#cards.get(targetId);
        targetCard?.classList.add(index >= this.#state.order.length ? 'is-drop-after' : 'is-drop-before');
    }

    #onDrop(event) {
        if (!this.#draggedId) return;
        event.preventDefault();
        const entry = this.#draggedId;
        const rawIndex = this.#dropIndex(event.clientY);
        const currentIndex = this.#state.order.indexOf(entry);
        const targetIndex = rawIndex > currentIndex ? rawIndex - 1 : rawIndex;
        const next = moveSectionTo(this.#state, entry, targetIndex);
        this.#onDragEnd(this.#cards.get(entry));
        if (next === this.#state) return;
        this.#hideRowNotice();
        const label = this.#entryLabel(entry);
        this.#state = next;
        this.#reorderCards();
        this.#announceMove(entry, label);
        const dissolved = this.#repairRows();
        this.#syncAll();
        if (dissolved) this.#announceRowDissolved(dissolved);
        this.#onChange();
    }

    #onDragEnd(card) {
        this.#draggedId = null;
        if (card) {
            card.draggable = false;
            card.classList.remove('is-dragging');
        }
        this.#cards.forEach((c) => c.classList.remove('is-drop-before', 'is-drop-after'));
    }

    /** Insertion index (0..n) for a pointer at the given viewport Y coordinate. */
    #dropIndex(clientY) {
        const order = this.#state.order;
        for (let i = 0; i < order.length; i++) {
            const rect = this.#cards.get(order[i]).getBoundingClientRect();
            if (clientY < rect.top + rect.height / 2) return i;
        }
        return order.length;
    }

    // ---- synchronisation ---------------------------------------------------------------

    #syncAll() {
        if (!this.#panel) return;
        this.#hiddenInput.value = this.#compatible ? serializeCompositionState(this.#state) : '';
        this.#state.order.forEach((entry, index) => {
            const card = this.#cards.get(entry);
            const section = this.#section(parseEntry(entry).sectionId);
            if (isRepeatable(section)) {
                this.#syncItemCard(section, entry, card, index);
            } else {
                const problems = sectionContentProblems(
                    section, this.#state.sections[entry].layout, this.#fields, (key) => this.#isFilled(key));
                this.#syncCard(section, card, index, problems);
            }
        });
        this.#syncAdders();
        this.#syncBlockPickers();
        this.#syncRows();
    }

    /**
     * Marks the two cards of every shared row, tags them with the row number and their side, and
     * gives them the row's colour.
     * @returns {import('../../core/page/composition.core.js').CompositionRow[]}
     */
    #syncRows() {
        const rows = compositionRows(this.#composition, this.#state, (key) => this.#isChecked(key));
        this.#syncedRows = rows;
        const places = new Map();
        rows.forEach((row, index) => row.entries.forEach((entry, position) => {
            places.set(entry, {number: index + 1, shared: row.entries.length > 1, position});
        }));
        const tones = rowTones(rows);
        for (const [entry, card] of this.#cards) {
            const place = places.get(entry);
            const shared = Boolean(place?.shared);
            card.classList.toggle('is-row-first', shared && place.position === 0);
            card.classList.toggle('is-row-second', shared && place.position > 0);
            setRowTone(card, tones.get(entry));
            const tag = card.querySelector('.section-card-placement');
            tag.hidden = !shared;
            tag.textContent = shared
                ? tpl(place.position === 0 ? 'pageForm.rowLeft' : 'pageForm.rowRight', {n: place.number})
                : '';
            const pair = card.querySelector('.section-pair-previous');
            if (pair) pair.disabled = pairablePrevious(this.#composition, this.#state, entry, (key) => this.#isChecked(key)) === null;
            const bandGroup = card.querySelector('.section-choice--band');
            if (bandGroup) {
                bandGroup.hidden = shared;
                bandGroup.querySelectorAll('.choice-input').forEach((input) => {
                    input.checked = input.value === rowBand(this.#composition, this.#state, entry);
                });
            }
        }
        this.#syncRowFrames(rows, tones);
        this.#syncOutline(rows);
        return rows;
    }

    /** Keeps the sticky page outline in step with the rows and the currently expanded card. */
    #syncOutline(rows = compositionRows(this.#composition, this.#state, (key) => this.#isChecked(key))) {
        if (!this.#outline) return;
        const tones = rowTones(rows);
        this.#outline.update({
            rows: rows.map((row) => row.entries.map((entry) => ({
                entry,
                label: this.#entryLabel(entry),
                span: entrySpan(this.#state, entry),
                tone: tones.get(entry),
            }))),
            active: this.#activeEntry,
        });
    }

    /** Expands a card from the outline, scrolls it into view and focuses its edit toggle. */
    #jumpTo(entry) {
        this.#expand(entry);
        const card = this.#cards.get(entry);
        card.scrollIntoView?.({block: 'nearest'});
        card.querySelector('.section-edit').focus();
    }

    /**
     * Keeps one frame above every shared row, in the list, up to date with the row and in its
     * colour from `tones` (as {@link rowTones} gives them for `rows`).
     */
    #syncRowFrames(rows, tones) {
        const shared = rows.filter((row) => row.entries.length > 1);
        const firsts = new Set(shared.map((row) => row.entries[0]));
        for (const [first, frame] of this.#rowFrames) {
            if (!firsts.has(first)) {
                frame.element.remove();
                this.#rowFrames.delete(first);
            }
        }
        for (const row of shared) {
            const [first, second] = row.entries;
            let frame = this.#rowFrames.get(first);
            // A frame's callbacks capture its two entries, so a row whose second block changed gets a new frame.
            if (frame && frame.second !== second) {
                frame.element.remove();
                frame = undefined;
            }
            if (!frame) {
                frame = {
                    ...createRowFrame({
                        onSplit: (span) => this.#setSplit(first, second, span),
                        onSwap: () => this.#swapRow(first, second),
                        onDissolve: () => this.#dissolveRow([first, second]),
                    }),
                    second,
                };
                if (hasBands(this.#composition)) {
                    frame.bandSlot.appendChild(createBandGroup(this.#composition, `__wb_rowband_${first}`,
                        (bandId) => this.#updateChoice(selectBand(this.#state, first, bandId))));
                }
                this.#rowFrames.set(first, frame);
            }
            // Moving an element that is already in place would still detach it, which blurs the
            // focused split handle and releases its pointer capture in the middle of a drag.
            const card = this.#cards.get(first);
            if (frame.element.nextElementSibling !== card) card.insertAdjacentElement('beforebegin', frame.element);
            if (hasBands(this.#composition)) {
                frame.bandSlot.querySelectorAll('.choice-input').forEach((input) => {
                    input.checked = input.value === rowBand(this.#composition, this.#state, first);
                });
            }
            setRowTone(frame.element, tones.get(first));
            frame.update({
                rowNumber: rows.indexOf(row) + 1,
                firstLabel: this.#entryLabel(first),
                secondLabel: this.#entryLabel(second),
                span: entrySpan(this.#state, first),
                ...splitBounds(this.#composition, first, second),
            });
        }
    }

    #setSplit(first, second, span) {
        this.#hideRowNotice();
        this.#updateChoice(setSplit(this.#composition, this.#state, first, second, span));
    }

    #swapRow(first, second) {
        this.#hideRowNotice();
        this.#rowFrames.get(first)?.element.remove();
        this.#rowFrames.delete(first);
        this.#state = swapRow(this.#state, first, second);
        this.#reorderCards();
        this.#syncAll();
        this.#rowFrames.get(second).element.querySelector('.section-row-swap').focus();
        this.#announce(t('pageForm.rowSwapped'));
        this.#onChange();
    }

    #dissolveRow(entries) {
        this.#hideRowNotice();
        this.#state = dissolveRow(this.#state, entries);
        this.#syncAll();
        this.#cards.get(entries[0]).querySelector('.section-edit').focus();
        this.#announceRowDissolved(entries[0]);
        this.#onChange();
    }

    /**
     * Applies the row rules after the order or the visible sections changed, against the rows of
     * the last sync (the state and visibility before the change), so a change can dissolve rows
     * but never create one. Before the first sync nothing has changed since the state was set, so
     * its own rows are the reference.
     * @returns {string | null} the first entry returned to the full width, for the caller to
     *   announce and to show the notice above; null when no row was dissolved
     */
    #repairRows() {
        const isChecked = (key) => this.#isChecked(key);
        const previousRows = this.#syncedRows ?? compositionRows(this.#composition, this.#state, isChecked);
        const {state, dissolved} = repairRows(this.#composition, this.#state, isChecked, previousRows);
        this.#state = state;
        return dissolved[0]?.[0] ?? null;
    }

    #syncCard(section, card, index, problems) {
        const choice = this.#state.sections[section.id];
        this.#syncCardControls(section, card, index, choice, section.label);

        const visible = visibleFieldKeys(section, choice.layout, this.#fields);
        const required = new Set(requiredFieldKeys(section, choice.layout, this.#fields, (key) => this.#isFilled(key)));
        for (const group of card.querySelectorAll('.section-card-fields > .field-group')) {
            const key = group.dataset.fieldKey;
            group.dataset.layoutVisible = String(visible.has(key));
            syncFieldGroupVisibility(group);
            const label = group.querySelector(':scope > label') || group.querySelector('.image-card-label, .table-card-label');
            if (label) syncRequiredIndicator(label, required.has(key));
        }

        const enabled = isSectionEnabled(section, (key) => this.#isChecked(key));
        card.classList.toggle('is-disabled', !enabled);
        this.#syncSummary(section, card, visible, enabled);
        this.#syncInstruction(card, problems, enabled, (key) => this.#fieldsByKey.get(key)?.label || key);
    }

    /**
     * Keeps an item card in step with its item: title, choices, the fields its layout shows and
     * requires, summary, menu limits and content problems.
     */
    #syncItemCard(section, ref, card, index) {
        const choice = this.#state.items[ref];
        const editor = this.#itemEditors.get(ref);
        const itemFields = section.repeatable.fields;
        const label = this.#entryLabel(ref);
        card.querySelector('.section-card-label').textContent = label;
        card.querySelector('.section-drag-handle').title = tpl('pageForm.sectionDragHandle', {label});
        card.querySelector('.section-item-more').setAttribute('aria-label', tpl('pageForm.itemMore', {label}));
        card.querySelector('.section-item-confirm-text').textContent = tpl('pageForm.itemDeleteConfirm', {label});
        this.#syncCardControls(section, card, index, choice, label);

        const isFilled = (key) => editor.isFilled(key);
        const visible = visibleFieldKeys(section, choice.layout, itemFields);
        editor.setVisibleFields(visible);
        editor.setRequiredFields(requiredFieldKeys(section, choice.layout, itemFields, isFilled));
        this.#syncItemMenu(section, card);
        this.#syncItemSummary(section, card, editor, visible);
        const labels = new Map(itemFields.map((field) => [field.key, field.label]));
        this.#syncInstruction(card, sectionContentProblems(section, choice.layout, itemFields, isFilled, ref), true,
            (key) => labels.get(key));
    }

    /** Move buttons, edit label, choice tiles and choice summary, shared by both card kinds. */
    #syncCardControls(section, card, index, choice, label) {
        const up = card.querySelector('.section-move-up');
        const down = card.querySelector('.section-move-down');
        up.disabled = index === 0;
        down.disabled = index === this.#state.order.length - 1;
        up.setAttribute('aria-label', tpl('pageForm.sectionMoveUp', {label}));
        down.setAttribute('aria-label', tpl('pageForm.sectionMoveDown', {label}));
        const edit = card.querySelector('.section-edit');
        edit.textContent = edit.getAttribute('aria-expanded') === 'true' ? t('pageForm.sectionClose') : t('pageForm.sectionEdit');

        for (const kind of ['layout', 'appearance']) {
            card.querySelectorAll(`.section-choice--${kind} .choice-input`).forEach((input) => {
                input.checked = input.value === choice[kind];
            });
        }
        const showsAppearance = appearanceApplies(section, choice.layout);
        const appearanceGroup = card.querySelector('.section-choice--appearance');
        if (appearanceGroup) appearanceGroup.hidden = !showsAppearance;
        this.#syncChoiceSummary(section, card, layoutFor(section, choice.layout), showsAppearance ? choice.appearance : null);
    }

    /** Duplicate is unavailable at the section's maximum, delete at its minimum. */
    #syncItemMenu(section, card) {
        card.querySelector('.section-item-duplicate').disabled = this.#atMaximum(section);
        const atMinimum = itemCount(this.#state, section.id) <= this.#minItems(section);
        card.querySelector('.section-item-delete').disabled = atMinimum;
        const hint = card.querySelector('.section-item-menu-hint');
        hint.hidden = !atMinimum;
        hint.textContent = atMinimum ? tpl('pageForm.itemMinimum', {min: this.#minItems(section)}) : '';
    }

    /** Label and accessible name of every add button, and a hint (an error after a failed submit) at the count limits. */
    #syncAdders() {
        for (const section of this.#repeatableSections()) {
            const adder = this.#adders.get(section.id);
            const add = adder.querySelector('.section-item-add');
            const hint = adder.querySelector('.section-item-add-hint');
            const count = itemCount(this.#state, section.id);
            const max = this.#maxItems(section);
            add.querySelector('.section-item-add-label').textContent = this.#itemLabel(section);
            add.setAttribute('aria-label', tpl('pageForm.itemAdd', {label: this.#itemLabel(section)}));
            add.disabled = this.#atMaximum(section);
            let text = '';
            if (add.disabled) {
                text = tpl('pageForm.itemMaximum', {max});
            } else if (count < this.#minItems(section)) {
                text = tpl('pageForm.itemMinimum', {min: this.#minItems(section)});
            }
            hint.textContent = text;
            hint.hidden = text === '';
            hint.classList.toggle('is-error', this.#showErrors && (count > max || count < this.#minItems(section)));
            if (text) {
                add.setAttribute('aria-describedby', hint.id);
            } else {
                add.removeAttribute('aria-describedby');
            }
        }
    }

    /**
     * Disables a block in every "add block below" picker while its section is at its maximum,
     * described by the section's add hint, which then names the maximum.
     */
    #syncBlockPickers() {
        for (const option of this.#list.querySelectorAll('.section-block-picker-option')) {
            const {sectionId} = option.dataset;
            option.disabled = this.#atMaximum(this.#section(sectionId));
            if (option.disabled) {
                option.setAttribute('aria-describedby', this.#adders.get(sectionId).querySelector('.section-item-add-hint').id);
            } else {
                option.removeAttribute('aria-describedby');
            }
        }
    }

    /** Names the selected layout and appearance, only for sections that offer a choice. */
    #syncChoiceSummary(section, card, layout, appearanceId) {
        const element = card.querySelector('.section-card-choice');
        const appearance = section.appearances?.find((option) => option.id === appearanceId);
        const parts = [
            (section.layouts?.length ?? 0) > 1 ? layout?.label : null,
            appearance?.label,
        ].filter(Boolean);
        element.hidden = parts.length === 0;
        element.textContent = parts.join(' · ');
    }

    #syncSummary(section, card, visible, enabled) {
        const status = card.querySelector('.section-card-status');
        const excerpt = card.querySelector('.section-card-excerpt');
        const hasContent = [...visible].some((key) => this.#fieldsByKey.has(key) && this.#isFilled(key));
        if (!enabled) {
            status.textContent = t('pageForm.sectionDisabled');
        } else if (!hasContent) {
            status.textContent = t('pageForm.sectionEmpty');
        } else {
            status.textContent = section.enabledByField ? t('pageForm.sectionEnabled') : '';
        }
        status.hidden = status.textContent === '';

        const textKey = [...visible].find((key) => {
            return isTextFieldType(this.#fieldsByKey.get(key)?.type) && this.#isFilled(key);
        });
        this.#showExcerpt(excerpt, textKey ? this.#input(textKey).value : '');
        const imageKey = [...visible].find((key) => this.#fieldsByKey.get(key)?.type === FieldType.IMAGE && this.#isFilled(key));
        const input = imageKey ? this.#input(imageKey) : null;
        this.#syncThumbnail(card, imageKey ? {file: input.files?.[0], path: input.dataset.existingPath} : null);
    }

    #syncItemSummary(section, card, editor, visible) {
        const types = new Map(section.repeatable.fields.map((field) => [field.key, field.type]));
        const filled = [...visible].filter((key) => types.has(key) && editor.isFilled(key));
        const status = card.querySelector('.section-card-status');
        status.textContent = filled.length === 0 ? t('pageForm.sectionEmpty') : '';
        status.hidden = filled.length > 0;

        const item = editor.item;
        // The label field already titles the card, so the excerpt comes from another text field.
        const textKey = filled.find((key) => isTextFieldType(types.get(key)) && key !== section.repeatable.itemLabelField);
        this.#showExcerpt(card.querySelector('.section-card-excerpt'), textKey ? item[textKey] : '');
        const imageKey = filled.find((key) => types.get(key) === FieldType.IMAGE);
        const pending = editor.files.find((entry) => entry.fieldKey === imageKey);
        this.#syncThumbnail(card, imageKey ? {file: pending?.file, path: item[imageKey]} : null);
    }

    #showExcerpt(excerpt, value) {
        const text = value.trim();
        excerpt.textContent = text.length > EXCERPT_LENGTH ? `${text.slice(0, EXCERPT_LENGTH)}…` : text;
    }

    /**
     * Uses an already-available image preview; never loads live embeds such as maps.
     * @param {HTMLElement} card
     * @param {{file?: File, path?: string} | null} image A pending file, else the stored path; null hides it
     */
    #syncThumbnail(card, image) {
        const thumbnail = card.querySelector('.section-card-thumbnail');
        if (!image) {
            thumbnail.hidden = true;
            return;
        }
        const {file, path} = image;
        const source = file ? `file:${file.name}:${file.size}` : `path:${path}`;
        if (thumbnail.dataset.source === source) {
            thumbnail.hidden = false;
            return;
        }
        thumbnail.dataset.source = source;
        const previous = this.#thumbnailUrls.get(card);
        if (previous) URL.revokeObjectURL(previous);
        if (file) {
            const url = URL.createObjectURL(file);
            this.#thumbnailUrls.set(card, url);
            thumbnail.src = url;
        } else {
            this.#thumbnailUrls.delete(card);
            this.#onAssetRequest(path, thumbnail);
        }
        thumbnail.hidden = false;
    }

    #syncInstruction(card, problems, enabled, labelOf) {
        const instruction = card.querySelector('.section-card-instruction');
        instruction.replaceChildren();
        if (problems.length === 0) {
            instruction.hidden = true;
            instruction.classList.remove('is-error');
            instruction.removeAttribute('role');
            return;
        }
        for (const problem of problems) {
            const line = document.createElement('p');
            const labels = problem.keys.map(labelOf).join(', ');
            line.textContent = tpl(problem.type === 'requiredAny' ? 'pageForm.sectionNeedsAny' : 'pageForm.sectionNeedsContent', {fields: labels});
            instruction.appendChild(line);
        }
        instruction.hidden = false;
        const isError = this.#showErrors && enabled;
        instruction.classList.toggle('is-error', isError);
        if (isError) {
            instruction.setAttribute('role', 'alert');
        } else {
            instruction.removeAttribute('role');
        }
    }

    // ---- value helpers -----------------------------------------------------------------

    #problems() {
        return validateComposition(this.#composition, this.#fields, this.#state,
            (key) => this.#isFilled(key), (key) => this.#isChecked(key),
            (ref) => this.#itemEditors.get(ref));
    }

    /** Repeatable sections whose item count is outside their limits, in declaration order. */
    #countProblems() {
        return this.#repeatableSections()
            .map((section) => ({section, count: itemCount(this.#state, section.id)}))
            .filter(({section, count}) => count < this.#minItems(section) || count > this.#maxItems(section))
            .map(({section, count}) => ({section, tooFew: count < this.#minItems(section)}));
    }

    #section(id) {
        return this.#composition.sections.find((section) => section.id === id);
    }

    #plainSections() {
        return this.#composition.sections.filter((section) => !isRepeatable(section));
    }

    #repeatableSections() {
        return this.#composition.sections.filter(isRepeatable);
    }

    #itemLabel(section) {
        return section.repeatable.itemLabel || section.label;
    }

    #minItems(section) {
        return section.repeatable.minItems ?? 0;
    }

    /** @returns {number} Infinity when the section sets no maximum */
    #maxItems(section) {
        return section.repeatable.maxItems ?? Infinity;
    }

    #atMaximum(section) {
        return itemCount(this.#state, section.id) >= this.#maxItems(section);
    }

    #itemIds(sectionId) {
        return this.#state.order
            .map(parseEntry)
            .filter((entry) => entry.sectionId === sectionId && entry.itemId !== null)
            .map((entry) => entry.itemId);
    }

    #addButton(sectionId) {
        return this.#adders.get(sectionId).querySelector('.section-item-add');
    }

    /**
     * The card title of an entry: the section label for a plain section; for an item the value
     * of the section's `itemLabelField`, or "{itemLabel} {n}" when that is blank.
     */
    #entryLabel(entry) {
        const {sectionId} = parseEntry(entry);
        const section = this.#section(sectionId);
        if (!isRepeatable(section)) return section.label;
        const labelField = section.repeatable.itemLabelField;
        const value = labelField ? this.#itemEditors.get(entry).item[labelField] : '';
        if (value) return value;
        const position = this.#itemIds(sectionId).indexOf(parseEntry(entry).itemId) + 1;
        return tpl('pageForm.itemNumbered', {label: this.#itemLabel(section), n: position});
    }

    /**
     * The given state, when it still fits the composition together with the given item values;
     * else null.
     * @param {import('../../shell/types/user.types.js').CompositionState} state
     * @param {Map<string, ItemValues>} values
     */
    #carriedState(state, values) {
        const lists = {};
        for (const section of this.#repeatableSections()) {
            lists[section.repeatable.dataKey] = state.order
                .filter((entry) => parseEntry(entry).sectionId === section.id)
                .map((ref) => values.get(ref)?.item ?? {[TABLE_ROW_ID_KEY]: parseEntry(ref).itemId});
        }
        const restored = restoreCompositionState(this.#composition, state, lists, () => true, false);
        return restored.compatible ? restored.state : null;
    }

    /**
     * Item values keyed by reference from page-data lists: the first item with each id.
     * @param {Object<string, unknown>} lists
     * @returns {Map<string, ItemValues>}
     */
    #itemValuesFromLists(lists) {
        const values = new Map();
        for (const section of this.#repeatableSections()) {
            const list = lists[section.repeatable.dataKey];
            for (const item of Array.isArray(list) ? list : []) {
                const ref = itemRef(section.id, String(item?.[TABLE_ROW_ID_KEY]));
                if (!values.has(ref)) values.set(ref, {item, files: []});
            }
        }
        return values;
    }

    #input(key) {
        return this.#root.querySelector(`[name="${key}"]`);
    }

    #isFilled(key) {
        const input = this.#input(key);
        if (!input) return false;
        if (input.localName === 'table-field') {
            return input.rows.length > 0;
        }
        if (input.type === 'file') {
            return (input.files && input.files.length > 0) || Boolean(input.dataset.existingPath);
        }
        if (input.type === 'checkbox') {
            return input.checked;
        }
        return String(input.value ?? '').trim().length > 0;
    }

    #isChecked(key) {
        const input = this.#input(key);
        if (!input) return false;
        if (input.type === 'checkbox') return input.checked;
        const raw = String(input.value ?? '').trim().toLowerCase();
        return raw === 'true' || raw === '1' || raw === 'yes' || raw === 'on';
    }
}
